/* eslint-disable indent */
import { useForm } from 'react-hook-form'
import { MouseEventHandler, useEffect, useMemo, useState } from 'react'
import {
  PatientTransportDto,
  CreatePatientTransportDto,
  UpdateAmbulanceDriverDto,
  MedicalOrder,
  PatientTransportStatus,
} from '../../../services/api'
import { useSelector } from '../../../state/store'
import { useAppDispatch } from '../../../hooks/reduxHooks'
import {
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
} from '@mui/material'

import {
  createPatientTransport,
  findAmbulanceCompanies,
  updatePatientTransport,
} from '../../../state/thunks/ambulanceThunk'
import { AmbulanceForm } from './AmbulanceForm'
import moment from 'moment'
import { capitalize } from 'lodash'
import { FormattedMessage } from 'react-intl'
import { TextInput } from '../../../components/inputs'
import { OrderDto } from '../../../services/dtos'
import { yupResolver } from '@hookform/resolvers/yup'
import { patientTransportSchema } from '@utils/schemas'

type AddDriverCompanyProps = {
  row: MedicalOrder | OrderDto
  isOrder?: boolean
  onClose?: () => void
  onCancelButtonPress?: (
    ev: MouseEventHandler<HTMLButtonElement> | undefined,
  ) => void
}

export function TransportForm({
  row,
  onClose,
  isOrder,
}: AddDriverCompanyProps) {
  const {
    control,
    handleSubmit,
    register,
    formState: { errors },
  } = useForm<CreatePatientTransportDto>({
    resolver: yupResolver(patientTransportSchema),
    defaultValues: {
      ...row?.patientTransport,
      companyId: row?.patientTransport?.ambulanceCompanyId,
    },
  })
  const [transportLinkType, setTransportLinkType] = useState('ambulance/taxi')
  const [isEditing, setIsEditing] = useState(!row?.patientTransport?.id)

  const dispatch = useAppDispatch()

  const companies = useSelector((state) => state.ambulance.data)
  const [isAmbulanceFormOpen, setIsAmbulanceFormOpen] = useState(false)

  const handleOpenAmbulanceForm = () => {
    setIsAmbulanceFormOpen(true)
  }
  const handleCloseAmbulanceForm = () => {
    setIsAmbulanceFormOpen(false)
    // Optionally refresh company list here if needed
  }
  const companyOptions: {
    value: string | number
    label: string
  }[] = useMemo(() => {
    return companies.map((company) => ({
      value: company.id,
      label: company.name,
    }))
  }, [companies])

  const onSubmit = (values: PatientTransportDto) => {
    if (row.patientTransport?.id) {
      const updateAmbulanceCompanyDto: UpdateAmbulanceDriverDto = {
        ...values,
      }
      dispatch(
        updatePatientTransport({
          id: row.patientTransport.id,
          dto: updateAmbulanceCompanyDto,
        }),
      ).then(() => {
        setIsEditing(false)
      })
    } else {
      const createAmbulanceCompanyDto: CreatePatientTransportDto = {
        ...values,
      }
      if (isOrder) {
        createAmbulanceCompanyDto.orderId = row.id
      } else {
        createAmbulanceCompanyDto.medicalOrderId = row.id
      }
      dispatch(createPatientTransport(createAmbulanceCompanyDto))
        .unwrap()
        .then(() => {
          setIsEditing(false)
        })
    }
    if (onClose) {
      onClose()
    }
  }

  const updateTransportStatus = (status: PatientTransportStatus) => {
    const transportId = isOrder
      ? row.patientTransport?.id
      : row.patientTransportId

    if (!transportId) {
      return
    }
    dispatch(updatePatientTransport({ id: transportId, dto: { status } }))
  }

  const ambulanceTaxiOptions = [
    { value: 'ambulance', label: 'Ambulance' },
    { value: 'vsl', label: 'VSL' },
  ]

  const privateOptions = [
    { value: 'friend', label: 'Proche' },
    { value: 'relative', label: 'Famille' },
    { value: 'other', label: 'Autre' },
  ]
  useEffect(() => {
    if (!row) {
      return
    }

    const r = privateOptions.find(
      ({ value }) => value === row.patientTransport?.transportType,
    )
      ? 'private'
      : 'ambulance/taxi'

    setTransportLinkType(r)
  }, [row])

  useEffect(() => {
    dispatch(findAmbulanceCompanies())
  }, [])

  const getExamStr = () => {
    if (isOrder) {
      const order = row as OrderDto
      const exams: string[] = []
      order.medicalOrders.forEach((o) => {
        if (o.exam) {
          exams.push(`${o.exam.label} (${o.exam.code})`)
        }
      })
      return exams.join(', ')
    }
    return `${row.exam?.label} (${row.exam?.code})`
  }

  const examsListStr = getExamStr()

  return (
    <Grid
      container
      spacing={1}
      sx={{
        padding: 2,
        borderRadius: 5,
      }}
    >
      {isEditing ? (
        <Paper elevation={0}>
          <Grid container rowSpacing={2} columnSpacing={2}>
            <Grid item xs={12}>
              <RadioGroup
                row
                value={transportLinkType}
                onChange={(e) => setTransportLinkType(e.target.value)}
              >
                <FormControlLabel
                  value="ambulance/taxi"
                  control={<Radio />}
                  label="Ambulance/Taxi"
                />
                <FormControlLabel
                  value="private"
                  control={<Radio />}
                  label="Personnel"
                />
              </RadioGroup>
            </Grid>

            {transportLinkType === 'ambulance/taxi' && (
              <>
                <Grid item xs={12}>
                  <InputLabel id="company-label">
                    Choisir une société
                  </InputLabel>
                  <Select
                    variant="outlined"
                    {...register('companyId')}
                    defaultValue={row?.patientTransport?.ambulanceCompanyId}
                    fullWidth
                  >
                    {companyOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>
                <Grid item xs={12}>
                  <Button
                    onClick={handleOpenAmbulanceForm}
                    color="primary"
                    variant="outlined"
                  >
                    + Ajouter une société
                  </Button>
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <InputLabel id="transportType">Type de transport</InputLabel>
              <Select
                {...register('transportType')}
                variant="outlined"
                defaultValue={row?.patientTransport?.transportType}
                fullWidth
              >
                {transportLinkType === 'ambulance/taxi' &&
                  ambulanceTaxiOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                {transportLinkType === 'private' &&
                  privateOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
              </Select>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextInput
                variant="outlined"
                name="name"
                errors={errors}
                control={control}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextInput
                variant="outlined"
                name="phoneNumber"
                errors={errors}
                control={control}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextInput
                variant="outlined"
                name="plateNumber"
                errors={errors}
                control={control}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextInput
                variant="outlined"
                name="comments"
                label="Comments"
                control={control}
                errors={errors}
                multiline
                rows={4}
              />
            </Grid>
            <Grid container xs={12} justifyContent="flex-end" gap={2} mt={2}>
              <Button
                onClick={() => {
                  setIsEditing(false)
                }}
                variant="outlined"
              >
                Fermer
              </Button>
              <Button
                variant="contained"
                color="secondary"
                onClick={handleSubmit(onSubmit)}
              >
                Sauvegarder
              </Button>
            </Grid>
          </Grid>
        </Paper>
      ) : (
        <>
          <TableContainer component={Paper}>
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell
                    component="th"
                    scope="row"
                    style={{ fontWeight: 'bold' }}
                  >
                    Patient
                  </TableCell>
                  <TableCell>
                    {capitalize(row.patient.firstName)}{' '}
                    {row.patient.lastName.toUpperCase()}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    component="th"
                    scope="row"
                    style={{ fontWeight: 'bold' }}
                  >
                    Date de naissance
                  </TableCell>
                  <TableCell>
                    {moment(row.patient.birthDate).format('DD/MM/YYYY')}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    component="th"
                    scope="row"
                    style={{ fontWeight: 'bold' }}
                  >
                    Examen
                  </TableCell>
                  <TableCell>{examsListStr}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          <Grid
            item
            xs={12}
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 2,
              marginTop: 1,
              marginBottom: 1,
            }}
          >
            <h1>Transport</h1>
            {row.patientTransport?.status === PatientTransportStatus.Called ? (
              <Chip
                color="success"
                label="Appelé"
                sx={{
                  color: 'white',
                  fontWeight: 'bold',
                }}
              />
            ) : (
              <Chip
                color="error"
                label="Non appelé"
                sx={{
                  color: 'white',
                  fontWeight: 'bold',
                }}
              />
            )}
          </Grid>
          <TableContainer component={Paper}>
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell
                    component="th"
                    scope="row"
                    style={{ fontWeight: 'bold' }}
                  >
                    Nom:
                  </TableCell>
                  <TableCell>{row.patientTransport?.name}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    component="th"
                    scope="row"
                    style={{ fontWeight: 'bold' }}
                  >
                    Téléphone:
                  </TableCell>
                  <TableCell>{row.patientTransport?.phoneNumber}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    component="th"
                    scope="row"
                    style={{ fontWeight: 'bold' }}
                  >
                    Numéro de plaque:
                  </TableCell>
                  <TableCell>{row.patientTransport?.plateNumber}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    component="th"
                    scope="row"
                    style={{ fontWeight: 'bold' }}
                  >
                    Commentaires:
                  </TableCell>
                  <TableCell>{row.patientTransport?.comments}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    component="th"
                    scope="row"
                    style={{ fontWeight: 'bold' }}
                  >
                    Type de contact:
                  </TableCell>
                  <TableCell>
                    <FormattedMessage
                      id={row.patientTransport?.transportType || 'Autre'}
                    />
                  </TableCell>
                </TableRow>

                {row.patientTransport?.ambulanceCompanyId && (
                  <>
                    <TableRow>
                      <TableCell
                        component="th"
                        scope="row"
                        style={{ fontWeight: 'bold' }}
                      >
                        Société:
                      </TableCell>
                      <TableCell>
                        {row.patientTransport.ambulanceCompany.name}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        component="th"
                        scope="row"
                        style={{ fontWeight: 'bold' }}
                      >
                        Adresse:
                      </TableCell>
                      <TableCell>
                        {row.patientTransport.ambulanceCompany.address}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        component="th"
                        scope="row"
                        style={{ fontWeight: 'bold' }}
                      >
                        Téléphone:
                      </TableCell>
                      <TableCell>
                        {row.patientTransport.ambulanceCompany.contact}
                      </TableCell>
                    </TableRow>
                  </>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <div className="flex flex-row gap-2 mt-3 justify-end w-full">
            <Button
              variant="outlined"
              onClick={() => {
                setIsEditing(true)
              }}
            >
              Modifier
            </Button>
            {row.patientTransport?.status === PatientTransportStatus.Called ? (
              <Button
                variant="contained"
                color="error"
                onClick={() => {
                  updateTransportStatus(PatientTransportStatus.NotCalled)
                }}
              >
                Marquer comme &ldquo;Non appelé&rdquo;
              </Button>
            ) : (
              <Button
                variant="contained"
                color="success"
                onClick={() => {
                  updateTransportStatus(PatientTransportStatus.Called)
                }}
              >
                Marquer comme &ldquo;Appelé&rdquo;
              </Button>
            )}
          </div>
        </>
      )}

      <Dialog open={isAmbulanceFormOpen} onClose={handleCloseAmbulanceForm}>
        <DialogTitle>Ajouter une ambulance</DialogTitle>
        <DialogContent>
          <AmbulanceForm onClose={handleCloseAmbulanceForm} />
        </DialogContent>
      </Dialog>
    </Grid>
  )
}
